import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import axios from "axios";
import { useSelector } from "react-redux";

export default function UnsubscribeConfirmation() {
  const server = useSelector((state: any) => state.server);
  const baseURL = `${server.serverProtocol}://${server.serverUrl}:${server.serverPort}/api/v1`;
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const queryParams: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });

  const queryKey = searchParams.toString();
  const storageKey = `unsubscribe:${queryKey}`;

  useEffect(() => {
    if (!queryKey) {
      setStatus("error");
      return;
    }

    const alreadyCalled = localStorage.getItem(storageKey);
    if (alreadyCalled) {
      setStatus("success");
      return;
    }
    console.log('baseUrl', baseURL);
    axios
      .post(`${baseURL}/unsubscribe`, queryParams)
      .then(() => {
        localStorage.setItem(storageKey, "true");
        setStatus("success");
      })
      .catch((error:any) => {
        setStatus("error");
        console.error("Unsubscribe request failed with error", error);
      });
  }, [queryKey]);

  const renderIcon = () => {
    if (status === "loading") {
      return <CircularProgress size={64} thickness={5} />;
    }

    if (status === "success") {
      return (
        <CheckCircleOutline
          sx={{ fontSize: 72, color: theme.palette.success.main, mb: 2 }}
        />
      );
    }

    return (
      <ErrorOutline
        sx={{ fontSize: 72, color: theme.palette.error.main, mb: 2 }}
      />
    );
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Processing Your Request
            </Typography>
            <Typography color="text.secondary">
              Please wait while we update your email preferences...
            </Typography>
          </>
        );
      case "success":
        return (
          <>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              You've successfully unsubscribed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We're sorry to see you go. You will no longer receive any emails
              from us.
            </Typography>
          </>
        );
      case "error":
        return (
          <>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We couldn't process your request. The link may be invalid or
              expired.
            </Typography>
          </>
        );
    }
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 2,
      }}
    >
      <Box>
        <Box mb={3}>{renderIcon()}</Box>
        {renderContent()}
      </Box>
    </Container>
  );
}
